const normalizeSceneOrder = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const sortSceneOrders = (orders = []) => (
  [...orders].sort((left, right) => {
    const leftNumber = Number(left);
    const rightNumber = Number(right);
    if (Number.isFinite(leftNumber) && Number.isFinite(rightNumber)) {
      return leftNumber - rightNumber;
    }
    return left.localeCompare(right);
  })
);

export const getMissingSceneOrders = (generation = {}) => {
  const safeGeneration = generation || {};
  const requiredScenes = Array.isArray(safeGeneration.required_scenes)
    ? safeGeneration.required_scenes
    : [];

  if (requiredScenes.length === 0) {
    const persistedOrders = safeGeneration.missing_scene_orders
      || safeGeneration.recovery?.missing_scene_orders
      || [];
    return sortSceneOrders(
      Array.from(new Set(persistedOrders.map(normalizeSceneOrder).filter(Boolean)))
    );
  }

  const requiredOrders = new Set(
    requiredScenes.map((scene) => normalizeSceneOrder(scene?.sceneOrder)).filter(Boolean)
  );
  const generatedOrders = new Set(
    (Array.isArray(safeGeneration.generated_scenes) ? safeGeneration.generated_scenes : [])
      .filter((scene) => {
        const status = String(scene?.status || 'completed').toLowerCase();
        return status !== 'failed' && Boolean(String(scene?.processedImageUrl || '').trim());
      })
      .map((scene) => normalizeSceneOrder(scene?.sceneOrder))
      .filter(Boolean)
  );

  return sortSceneOrders(
    Array.from(requiredOrders).filter((order) => !generatedOrders.has(order))
  );
};

export const buildGenerationScenePreviewItems = (generation = {}) => {
  const safeGeneration = generation || {};
  const requiredScenes = Array.isArray(safeGeneration.required_scenes)
    ? safeGeneration.required_scenes
    : [];
  const generatedScenes = Array.isArray(safeGeneration.generated_scenes)
    ? safeGeneration.generated_scenes
    : [];
  const generatedByOrder = new Map();

  generatedScenes.forEach((scene) => {
    const order = normalizeSceneOrder(scene?.sceneOrder);
    if (order) generatedByOrder.set(order, scene);
  });

  const requiredOrders = new Set();
  const previewItems = requiredScenes.map((requiredScene) => {
    const order = normalizeSceneOrder(requiredScene?.sceneOrder);
    requiredOrders.add(order);
    const generatedScene = generatedByOrder.get(order);
    const generatedStatus = String(generatedScene?.status || 'completed').toLowerCase();
    const generatedImageUrl = String(generatedScene?.processedImageUrl || '').trim();
    const isMissing = !generatedImageUrl || generatedStatus === 'failed';
    const maskUrl = String(
      requiredScene?.maskUrl
      || requiredScene?.mask_url
      || ''
    ).trim();

    if (!isMissing) {
      return {
        ...requiredScene,
        ...generatedScene,
        sceneOrder: order,
        isMissing: false,
      };
    }

    const scenePrompt = String(
      requiredScene?.scenePrompt
      || requiredScene?.scene_prompt
      || ''
    ).trim();

    return {
      ...requiredScene,
      sceneOrder: order,
      type: requiredScene?.sceneType === 'cover' ? 'cover' : 'Scene',
      processedImageUrl: maskUrl || requiredScene?.sceneUrl || '',
      missingMaskUrl: maskUrl,
      isMissing: true,
      // Prompt-based scenes have no mask; they regenerate directly from the
      // kid reference photo + scene prompt, same as the generation flow.
      canRegenerate: Boolean(maskUrl || scenePrompt),
    };
  });

  generatedScenes.forEach((scene) => {
    const order = normalizeSceneOrder(scene?.sceneOrder);
    if (!order || requiredOrders.has(order)) return;
    previewItems.push({ ...scene, sceneOrder: order, isMissing: false });
  });

  return previewItems.sort((left, right) => {
    const leftOrder = Number(left.sceneOrder);
    const rightOrder = Number(right.sceneOrder);
    if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder)) {
      return leftOrder - rightOrder;
    }
    return String(left.sceneOrder).localeCompare(String(right.sceneOrder));
  });
};

export const groupGenerationPreviewScenes = (scenes = [], generation = {}) => {
  const requiredByOrder = new Map((generation.required_scenes || []).map(scene => (
    [normalizeSceneOrder(scene.sceneOrder), scene]
  )));
  const isSpread = (scene) => {
    const snapshot = requiredByOrder.get(normalizeSceneOrder(scene.sceneOrder));
    const layout = scene.layoutMode || scene.effective_layout
      || snapshot?.layoutMode || generation.story?.layout_mode;
    return layout ? layout === 'spread' : generation.story_type === 'spread_story';
  };
  const isStandalone = scene => normalizeSceneOrder(scene.sceneOrder) === '0' || isSpread(scene);
  const groups = [];
  for (let index = 0; index < scenes.length; index++) {
    const current = scenes[index];
    const next = scenes[index + 1];
    if (isStandalone(current) || !next || isStandalone(next)) {
      groups.push([current]);
    } else {
      groups.push([current, next]);
      index++;
    }
  }
  return groups;
};

export const isMissingSceneRecoveryRunning = (generation = {}) => {
  const safeGeneration = generation || {};
  if (String(safeGeneration.recovery?.status || '').toLowerCase() !== 'running') {
    return false;
  }

  const lockExpiresAt = Date.parse(safeGeneration.recovery?.lock_expires_at || '');
  return !Number.isFinite(lockExpiresAt) || lockExpiresAt > Date.now();
};
